import { useEffect, useRef } from 'react';
import { toast } from '@/lib/toast';
import { useSyncStatusStore } from '@/data/sync/syncStatusStore';
import { useDataProvider } from '@/data/provider';
import { Button } from '@/components/ui/button';

function formatSyncContext(table?: string, recordId?: string): string {
  if (!table) return 'sync';
  if (!recordId) return table;
  return `${table} · ${recordId}`;
}

function formatStatusSuffix(cause?: unknown): string {
  if (!cause || typeof cause !== 'object') return '';
  if (!('status' in cause)) return '';
  const status = Number((cause as { status?: unknown }).status);
  return Number.isFinite(status) ? ` (HTTP ${status})` : '';
}

export function SyncStatusNotifier(): null {
  const { sync } = useDataProvider();
  const lastError = useSyncStatusStore((state) => state.lastError);
  const lastConflict = useSyncStatusStore((state) => state.lastConflict);
  const lastErrorRef = useRef<string | null>(null);
  const lastConflictRef = useRef<string | null>(null);

  useEffect(() => {
    if (!lastError) return;

    const key = [
      lastError.phase,
      lastError.table,
      lastError.recordId ?? '',
      lastError.message,
    ].join('|');

    if (lastErrorRef.current === key) return;
    lastErrorRef.current = key;

    const context = formatSyncContext(lastError.table, lastError.recordId);

    toast.error(
      <div className="flex flex-col gap-2 text-sm">
        <p>
          Sync fallita su {context}
          {formatStatusSuffix(lastError.cause)}.
        </p>
        <div>
          <Button size="sm" variant="outline" onClick={() => void sync.retrySync()}>
            Riprova sync
          </Button>
        </div>
      </div>
    );
  }, [lastError, sync]);

  useEffect(() => {
    if (!lastConflict) return;

    const key = [
      lastConflict.phase,
      lastConflict.table,
      lastConflict.recordId ?? '',
      lastConflict.message,
    ].join('|');

    if (lastConflictRef.current === key) return;
    lastConflictRef.current = key;

    const context = formatSyncContext(lastConflict.table, lastConflict.recordId);

    toast.warning(
      <div className="flex flex-col gap-2 text-sm">
        <p>Conflitto sync su {context}.</p>
        <div>
          <Button size="sm" variant="outline" onClick={() => void sync.retrySync()}>
            Riprova sync
          </Button>
        </div>
      </div>
    );
  }, [lastConflict, sync]);

  return null;
}
